import { Popover } from 'antd';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { useLocation } from 'react-router';
import styled from 'styled-components';
import { Button, Dropdown, DropdownButton, Flex, Icon, notification } from 'ui';
import moment from 'utils/cops-moment';
import { withRouter } from 'utils/withRouter';
import Sticky from '../../../common/sticky/sticky';
// eslint-disable-next-line
import ServiceCenterToggle from '../../../header/service-center-toggle/containers/service-center-toggle-container';
import UploadModal from '../../../header/upload-xml/components/upload-modal';
import settingsConfig from '../../settings-config';
import UploadCSVError from './upload-csv-error';

const Toolbar = styled(Flex)`
  height: 55px;
  background-color: rgb(245, 248, 250) !important;
  margin: 0 -20px;
  padding-left: 20px;
`;

export const StyledInfoIcon = styled(Icon)`
  margin-left: 5px;
  margin-top: 5px;
  vertical-align: middle;
`;

const ToolbarItem = styled.div`
  padding: 0 15px;
  ${p => !p.noBorder && `border-left: 1px solid ${p.theme.colors.darkThemeSecondary}`}
`;

const StyledSpan = styled.span`
  font-size: 13px;
  line-height: 2.5;
  margin-right: 5px;
`;

const StyledDropdown = styled(Dropdown)`
  width: 200px;
`;

const StyledInfo = styled.p`
  margin-top: 10px;
`;

const StyledInfoReference = styled.p`
  margin-top: 10px;
  font-size: 12px;
`;

const ErrorTitle = styled.p`
  font-weight: bold;
  font-size: 15px;
`;

const Buttons = styled(Flex)`
`;

const StyledDropdownButton = styled(DropdownButton)`
  height: 34px;
  margin-right: 15px;
  button {
    text-transform: uppercase;
  }
`;

const StyledButton = styled(Button)`
  text-transform: uppercase;
  color: white !important;
  background-color: ${p => p.theme.colors.theme} !important;
  &:hover {
    opacity: 0.7;
  }
  ${p => p.disabled && `
    opacity: 0.7;
  `}
  margin-right: 15px;
`;

const TooltipTitle = styled.p`
  font-weight: bold;
`;

const AbsenceToolbar = ({
  menuVisible,
  intl,
  type,
  plants,
  isLoadingPlants,
  fetchData,
  isLoadingData,
  history,
  setPlantData,
  downloadCSV,
  isUploading,
  uploadCSV,
  uploadSuccess,
  uploadFailure,
  serviceCenterId,
}) => {
  const [regions, setRegions] = useState([]);
  const [fses, setFses] = useState([]);
  const [plantValue, setPlantValue] = useState();
  const [regionValue, setRegionValue] = useState();
  const [fseValue, setFseValue] = useState();
  const [canAdd, setCanAdd] = useState(true);
  const [initialDataFetched, setInitialDataFetched] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const location = useLocation();

  const serviceCenterToggleStyle = `
    margin: 0 10px;
    width: 200px;
  `;

  const getLocaleFormat = () => {
    const localeData = moment.localeData();
    return {
      time: localeData.longDateFormat('LTS'),
      date: localeData.longDateFormat('L'),
    };
  };

  const renderRules = (formatter) => {
    const formats = getLocaleFormat();
    return (
      <>
        <div>
          <TooltipTitle>{formatter.formatMessage({ id: 'holidays.DESCRIPTION' })}</TooltipTitle>
          <p>{formatter.formatMessage({ id: 'holidays.DESCRIPTION_INSTRUCTION' })}</p>
        </div>
        <div>
          <TooltipTitle>{formatter.formatMessage({ id: 'holidays.START_DATE' })}</TooltipTitle>
          <p>
            {formatter.formatMessage(
              { id: 'holidays.START_INSTRUCTION' },
              { format: formats.date },
            )}
          </p>
        </div>
        <div>
          <TooltipTitle>{formatter.formatMessage({ id: 'holidays.START_TIME' })}</TooltipTitle>
          <p>
            {formatter.formatMessage(
              { id: 'holidays.START_INSTRUCTION' },
              { format: formats.time },
            )}
          </p>
        </div>
        <div>
          <TooltipTitle>{formatter.formatMessage({ id: 'holidays.DURATION_HOURS' })}</TooltipTitle>
          <p>{formatter.formatMessage({ id: 'holidays.DURATION_HOURS_INSTRUCTION' })}</p>
        </div>
        <div>
          <TooltipTitle>{formatter.formatMessage({ id: 'holidays.COPS_ID' })}</TooltipTitle>
          <p>{formatter.formatMessage({ id: 'holidays.COPS_ID_INSTRUCTION' })}</p>
        </div>
      </>
    );
  };

  useEffect(() => {
    if (uploadSuccess) {
      notification.success({
        message: <>
          <p>{intl.formatMessage({
            id: 'holidays.UPLOAD_SUCCESS',
          })}</p>
          <p>{intl.formatMessage({
            id: 'holidays.PROGRESS_SAP',
          })}</p>
        </>,
        placement: 'top',
      });
      if (type === settingsConfig.types.fse) {
        fetchData(fseValue);
      } else if (type === settingsConfig.types.region) {
        fetchData(regionValue);
      } else if (type === settingsConfig.types.plant) {
        fetchData(plantValue);
      }
      setIsModalVisible(false);
    }
  }, [uploadSuccess]);

  useEffect(() => {
    if (uploadFailure && Object.keys(uploadFailure).length) {
      let errorMsg =
        <>
          <ErrorTitle>{uploadFailure.title}</ErrorTitle>
          <StyledInfo>{uploadFailure.message}</StyledInfo>
          <StyledInfoReference>{uploadFailure.reference}</StyledInfoReference>
        </>;
      if (uploadFailure.errors.length) {
        errorMsg = <UploadCSVError error={uploadFailure} intl={intl} renderRules={renderRules} />;
      }

      notification.error({
        message: errorMsg,
        key: 'errorUploadNotification',
        placement: 'top',
        style: {
          width: uploadFailure.errors.length ? 680 : 500,
          marginLeft: uploadFailure.errors.length ? -70 : -60,
        },
        duration: 0,
      });
    }
  }, [uploadFailure]);

  const checkCanAdd = (value) => {
    setCanAdd(!!value);
  };

  const fetchAbsences = (value, changedType) => {
    if (changedType === type) {
      fetchData(value);
    } else if (changedType === settingsConfig.types.region) {
      const fsesList = regions?.find(r => r.id === value)?.items;
      if (fsesList?.length) {
        fetchData(fsesList[0].id);
      } else {
        fetchData(value);
      }
    } else if (changedType === settingsConfig.types.plant) {
      const regionList = plants?.find(r => r.id === value)?.items;
      if (regionList?.length) {
        const fsesList = regions?.find(r => r.id === regions[0].id)?.items;
        if (type === settingsConfig.types.fse && fsesList?.length) {
          fetchData(fsesList[0].id);
        } else {
          fetchData(regions[0].id);
        }
      } else {
        fetchData(value);
      }
    }
  };

  const handlePlantChange = (value) => {
    fetchAbsences(value, settingsConfig.types.plant);
    setPlantData(data => ({
      ...data,
      plant: plants?.find(p => p.id === value),
    }));
    setPlantValue(value);
    setRegions(plants?.find(p => p.id === value)?.items || []);
    if (type === settingsConfig.types.plant) {
      checkCanAdd(value);
    }
  };

  const handleRegionChange = (value) => {
    fetchAbsences(value, settingsConfig.types.region);
    setPlantData(data => ({
      ...data,
      region: regions?.find(r => r.id === value),
    }));
    setRegionValue(value);
    setFses(regions?.find(r => r.id === value)?.items || []);
    if (type === settingsConfig.types.region) {
      checkCanAdd(value);
    }
  };

  const handleFseChange = (value) => {
    fetchAbsences(value, settingsConfig.types.fse);
    setPlantData(data => ({
      ...data,
      fse: fses?.find(f => f.id === value),
    }));
    setFseValue(value);
    if (type === settingsConfig.types.fse) {
      checkCanAdd(value);
    }
  };

  useEffect(() => {
    setInitialDataFetched(false);
  }, [serviceCenterId]);

  // Don't use deepcompare here, the if statement will block the useEffect
  useEffect(() => {
    const info = location?.state?.holidayInfo;
    if (info?.length && plants?.length) {
      if (info?.length === 3) {
        setPlantValue(info?.find(i => i.type === settingsConfig.types.plant)?.id);
        setRegionValue(info?.find(i => i.type === settingsConfig.types.region)?.id);
        handleFseChange(info?.find(i => i.type === settingsConfig.types.fse)?.id);
      } else if (info?.length === 2) {
        setPlantValue(info?.find(i => i.type === settingsConfig.types.plant)?.id);
        handleRegionChange(info?.find(i => i.type === settingsConfig.types.region)?.id);
      } else if (info?.length === 1) {
        handlePlantChange(info?.find(i => i.type === settingsConfig.types.plant)?.id);
      }
      history.replace({ state: {} });
    }
  }, [location?.state?.holidayInfo, plants]);

  useEffect(() => {
    if (!initialDataFetched && plantValue) {
      setInitialDataFetched(true);
      if (type === settingsConfig.types.plant) {
        checkCanAdd(fseValue || regionValue || plantValue);
        fetchData(fseValue || regionValue || plantValue);
      }
    }
  }, [plantValue]);

  useEffect(() => {
    setPlantValue(plants[0]?.id);
    if (type === settingsConfig.types.plant) {
      checkCanAdd(plants[0]?.id);
    }
    setPlantData(data => ({
      ...data,
      plant: plants?.find(p => p.id === plants[0]?.id),
    }));
    setRegions(plants?.find(p => p.id === plants[0]?.id)?.items || []);
  }, [plants]);

  useEffect(() => {
    setRegionValue(regions[0]?.id);
    if (type === settingsConfig.types.region) {
      checkCanAdd(regions[0]?.id);
    }
    setPlantData(data => ({
      ...data,
      region: regions?.find(r => r.id === regions[0]?.id),
    }));
    setFses(regions?.find(r => r.id === regions[0]?.id)?.items || []);
  }, [regions]);

  useEffect(() => {
    setFseValue(fses[0]?.id);
    if (type === settingsConfig.types.fse) {
      checkCanAdd(fses[0]?.id);
    }
    setPlantData(data => ({
      ...data,
      fse: fses?.find(f => f.id === fses[0]?.id),
    }));
  }, [fses]);

  useEffect(() => {
    if (type === settingsConfig.types.fse) {
      checkCanAdd(fseValue);
      fetchData(fseValue || regionValue || plantValue);
    } else if (type === settingsConfig.types.region) {
      checkCanAdd(regionValue);
      const region = regions?.find(r => r.id === regionValue);
      setFses(region?.items || []);
      setFseValue(region?.items[0]?.id);
      fetchData(regionValue || plantValue);
    } else if (type === settingsConfig.types.plant) {
      checkCanAdd(plantValue);
      const plant = plants?.find(p => p.id === plantValue);
      const region = plant?.items[0];
      setRegions(plant?.items || []);
      setRegionValue(region?.id);
      setFses(region?.items || []);
      setFseValue(region?.items[0]?.id);
      if (initialDataFetched) {
        fetchData(plantValue);
      }
    }
  }, [type]);

  const openSidepanel = () => {
    history.push('edit-absence');
  };

  const getTooltipText = () =>
    <div>
      <div>
        <p><FormattedMessage id='holidays.UPLOAD_INSTRUCTION' />{':'}</p>
      </div>
      {renderRules(intl)}
    </div>;

  const getTechnicianId = () => {
    if (type === settingsConfig.types.plant) {
      return plantValue;
    } else if (type === settingsConfig.types.region) {
      return regionValue;
    } else if (type === settingsConfig.types.fse) {
      return fseValue;
    }
    return null;
  };

  const uploadFileRequest = ({ file }) => {
    const id = getTechnicianId();
    uploadCSV({
      technicianId: id,
      file,
    });
  };

  const csvOptions = [
    {
      text: intl.formatMessage({ id: 'holidays.MASS_UPLOAD' }),
      // eslint-disable-next-line
      onClick: () => setIsModalVisible(true),
    },
    {
      text: intl.formatMessage({ id: 'holidays.DOWNLOAD_CURRENT_DATA' }),
      onClick: () => {
        const id = getTechnicianId();
        downloadCSV({
          technicianId: id,
        });
      },
    },
  ];

  return (
    <>
      <Sticky
        menuVisible={menuVisible}
        translateYEnd='-106px'
      >
        <Toolbar aic jcfs ffrn>
          <ToolbarItem noBorder={true}>
            <StyledSpan>
              {intl.formatMessage({ id: 'holidays.CUSTOMER_CENTER' })}{':'}
            </StyledSpan>
            <ServiceCenterToggle
              needsRedirect={false}
              extraStyles={serviceCenterToggleStyle}
              onlyAdmin={true}
            />
          </ToolbarItem>
          <ToolbarItem>
            <StyledSpan>
              {intl.formatMessage({ id: 'holidays.PLANT' })}{':'}
            </StyledSpan>
            <StyledDropdown
              placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
              onChange={handlePlantChange}
              values={plants?.length > 0 ? plants : []}
              disabled={!plants || plants?.length === 0 || isLoadingPlants || isLoadingData}
              valueLabel='name'
              valueKey='id'
              value={plantValue}
            />
          </ToolbarItem>
          {type === settingsConfig.types.region || type === settingsConfig.types.fse ?
            <ToolbarItem>
              <StyledSpan>
                {intl.formatMessage({ id: 'holidays.REGION' })}{':'}
              </StyledSpan>
              <StyledDropdown
                placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
                onChange={handleRegionChange}
                values={regions?.length > 0 ? regions : []}
                disabled={!regions || regions?.length === 0 || isLoadingPlants || isLoadingData}
                valueLabel='name'
                valueKey='id'
                value={regionValue}
              />
            </ToolbarItem> :
            null
          }
          {type === settingsConfig.types.fse ?
            <ToolbarItem>
              <StyledSpan>
                {intl.formatMessage({ id: 'holidays.FSE' })}{':'}
              </StyledSpan>
              <StyledDropdown
                placeholder={intl.formatMessage({ id: 'order.groupDropdown.DROPDOWN_DEFAULT' })}
                onChange={handleFseChange}
                values={fses?.length > 0 ? fses : []}
                disabled={!fses || fses?.length === 0 || isLoadingPlants || isLoadingData}
                valueLabel='name'
                valueKey='id'
                value={fseValue}
              />
            </ToolbarItem> :
            null
          }
          <ToolbarItem>
            <Buttons ffrn>
              <StyledButton
                onClick={openSidepanel}
                disabled={isLoadingPlants || isLoadingData || !canAdd}
              >
                {intl.formatMessage({ id: 'holidays.ADD' })}
              </StyledButton>
              <StyledDropdownButton
                disabled={isLoadingPlants || isLoadingData}
                text={csvOptions[0].text}
                onClick={csvOptions[0].onClick}
                subItems={csvOptions}
                placement='bottomLeft'
                uppercase={true}
              />
              <Popover
                placement='bottomLeft'
                content={getTooltipText}
                getPopupContainer={node => node}
                overlayClassName='absence-toolbar-popover'
              >
                <div>
                  <StyledInfoIcon type='info-circle' onClick={e => e.preventDefault()} size='lg' />
                </div>
              </Popover>
            </Buttons>
          </ToolbarItem>
        </Toolbar>
      </Sticky>
      <UploadModal
        visible={isModalVisible}
        isLoading={isUploading}
        doFileUpload={uploadFileRequest}
        onClose={() => setIsModalVisible(false)}
        title={intl.formatMessage({
          id: 'holidays.UPLOAD_TITLE',
        })}
        fileType='holiday CSV'
      />
    </>
  );
};
AbsenceToolbar.propTypes = {
  menuVisible: PropTypes.bool,
  intl: PropTypes.object,
  type: PropTypes.string,
  plants: PropTypes.array,
  isLoadingPlants: PropTypes.bool,
  fetchData: PropTypes.func,
  isLoadingData: PropTypes.bool,
  history: PropTypes.object,
  setPlantData: PropTypes.func,
  downloadCSV: PropTypes.func,
  isUploading: PropTypes.bool,
  uploadCSV: PropTypes.func,
  uploadSuccess: PropTypes.bool,
  uploadFailure: PropTypes.object,
  serviceCenterId: PropTypes.string,
};

export default withRouter(injectIntl(AbsenceToolbar));
